'use client';

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useAuth } from '@/lib/context/AuthContext';
import { useToast } from '@/lib/context/ToastContext';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/Modal';
import RegistrationForm, { type RegistrationFormProps } from '@/components/registration/RegistrationForm';
import { fetchSpecialtyCatalog } from '@/lib/registration/client';
import type { SpecialtyOption } from '@/lib/specialties/types';
import type { QuestionAnswer } from '@/lib/tickets/questions';
import { useEnrollmentDetails } from './useEnrollmentDetails';

interface DetailsNeededPromptProps {
  enrollmentId: string;
  open: boolean;
  onClose: () => void;
  /** Fires once nothing is missing any more. */
  onCompleted?: () => void;
  /** Above a full-screen layer (the test overlay is z-50). */
  zIndex?: number;
  /**
   * When a fresh read finds nothing missing, hand back on its own (default).
   * false: the server refused again although the read says complete, so never
   * hand back automatically; show an error the participant can retry or close.
   */
  autoHandBack?: boolean;
}

type ServerErrors = NonNullable<RegistrationFormProps['serverErrors']>;

/** A modal with the form for exactly what the enrollment's ticket still needs. */
export default function DetailsNeededPrompt({ enrollmentId, open, onClose, onCompleted, zIndex, autoHandBack = true }: DetailsNeededPromptProps) {
  const { t } = useLanguage();
  const toast = useToast();
  const { user, refreshProfile } = useAuth();
  const { data, isError, isFetching, dataUpdatedAt, refetch, save } = useEnrollmentDetails(open ? enrollmentId : null);
  const [specialties, setSpecialties] = useState<SpecialtyOption[] | null>(null);
  const [serverErrors, setServerErrors] = useState<ServerErrors | null>(null);
  const [busy, setBusy] = useState(false);
  const [catalogFailed, setCatalogFailed] = useState(false);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  // The hand-back runs at most once per opening: a resubmit behind onCompleted must not run twice.
  const doneRef = useRef(false);
  // Only a read made after opening may say "complete": a cached answer can
  // predate a requirement the server just refused for.
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  useEffect(() => {
    if (!open) { setOpenedAt(null); return; }
    doneRef.current = false;
    setOpenedAt(Date.now());
    if (dataUpdatedAt > 0 && !isFetching) void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const fresh = openedAt !== null && !isFetching && dataUpdatedAt >= openedAt;

  // Opened for an enrollment that turns out complete: nothing to ask, so hand back at once.
  // Not while a save is in flight; the save hands back itself.
  const complete = open && !busy && fresh && data === null && !isError;
  const stuck = complete && !autoHandBack;
  useEffect(() => {
    if (!complete || !autoHandBack || doneRef.current) return;
    doneRef.current = true;
    if (onCompleted) onCompleted();
    else onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  // The form computes its shown fields on mount, so it opens only once the catalog is in.
  useEffect(() => {
    if (!open || specialties) return;
    let cancelled = false;
    setCatalogFailed(false);
    fetchSpecialtyCatalog()
      .then(catalog => {
        if (cancelled) return;
        if (catalog) setSpecialties(catalog);
        else setCatalogFailed(true);
      })
      .catch(() => { if (!cancelled) setCatalogFailed(true); });
    return () => { cancelled = true; };
  }, [open, specialties, catalogAttempt]);

  const failed = catalogFailed || (isError && !data) || stuck;
  const retry = () => {
    if (catalogFailed) setCatalogAttempt(n => n + 1);
    if (isError || stuck) void refetch();
  };

  const profileSaved = async () => {
    try {
      if (user) await refreshProfile(user.id, true);
    } catch {
      // The form already saved; a stale cached profile is not worth an error.
    }
  };

  const submit = async (answers: QuestionAnswer[]) => {
    setBusy(true);
    setServerErrors(null);
    try {
      const result = await save(answers);
      if (!result.ok) {
        setServerErrors(result.answerErrors
          ? { answerErrors: result.answerErrors }
          : { messageKey: 'errors.errorSavingData' });
        return;
      }
      if (result.stillMissing) {
        // The form remounts with the fresh missing set; say why it is still here.
        setServerErrors({ messageKey: 'additionalFields.validation.fillAllRequired' });
        return;
      }
      doneRef.current = true;
      toast.success(t('details.saved'));
      onCompleted?.();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const ready = !failed && data && specialties;
  // A save that leaves something missing brings a new set: show that set, not the old form.
  const formKey = data ? `${data.missingFields.join(',')}|${data.questions.map(q => q.id).join(',')}` : '';
  return (
    <Modal isOpen={open} onClose={() => { if (!busy) onClose(); }} title={t('details.promptTitle')} size="lg"
      closeOnBackdropClick={!busy} closeOnEscape={!busy} zIndex={zIndex}>
      {failed ? (
        <div role="alert" className="space-y-4 py-4 text-center">
          <p className="text-sm text-red-600">{t('details.loadFailed')}</p>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <Button type="button" variant="secondary" className="w-full sm:flex-1" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="button" variant="primary" className="w-full sm:flex-1" onClick={retry}>{t('common.retry')}</Button>
          </div>
        </div>
      ) : ready ? (
        <div className="max-h-[70vh] overflow-y-auto -mx-6 px-6">
          <RegistrationForm
            key={formKey}
            channel="site"
            contentId={data.contentId}
            ticketId={data.ticketId}
            needs={data}
            specialties={specialties}
            serverErrors={serverErrors}
            submitLabel={t('details.saveDetails')}
            busy={busy}
            onProfileSaved={profileSaved}
            onSubmit={submit}
            onCancel={onClose}
          />
        </div>
      ) : (
        <div role="status" aria-label={t('common.loading')} className="flex items-center justify-center py-8">
          <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-gray-900 rounded-full" />
        </div>
      )}
    </Modal>
  );
}
